<template>
  <div ref="containner" class="confetti-container">
    <canvas :id="'confettiCanvas_' + canvasId"></canvas>
  </div>
</template>

<script>
export default {
  name: "CanvasConfetti",
  props: {
    canvasId: { default: "" },
  },
  data() {
    return {
      canvas: null,
      ctx: null,
      W: 0,
      H: 0,

      bursts: [], // เก็บข้อมูลการระเบิดแต่ละครั้ง
      maxBursts: 10, // จำนวนการระเบิดพร้อมกันสูงสุด
      numParticlesPerBurst: 100, // จำนวนชิ้น confetti ต่อการระเบิดหนึ่งครั้ง
      burstDuration: 2000, // ระยะเวลาของการระเบิดแต่ละครั้ง (มิลลิวินาที)
      burstInterval: 500, // ช่วงเวลาระหว่างการระเบิดใหม่ (มิลลิวินาที)
      burstIntervalId: null, // ID สำหรับ clearInterval

      // Confetti properties (some reused from previous, adjusted for burst)
      gravity: 0.05,
      drag: 0.98, // แรงต้านอากาศที่ปรับให้ดูเหมือนระเบิด
      maxInitialVelocity: 7, // ความเร็วเริ่มต้นสูงสุดในการพุ่งออก

      animationFrameId: null, // สำหรับเก็บ ID ของ requestAnimationFrame เพื่อ cleanup

      resizeObserver: null,
    };
  },
  mounted() {
    this.initCanvas();
    // เริ่มต้น ResizeObserver
    this.initResizeObserver();
    this.scheduleNewBursts(); // เริ่มสร้างการระเบิดแบบวนซ้ำ
    this.startAnimation(); // **** เพิ่มโค้ดส่วนนี้เพื่อเริ่ม Animation Loop ****
  },
  beforeDestroy() {
    // ทำความสะอาด ResizeObserver
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }

    // ทำความสะอาด Event Listener และ Animation frame ก่อน Component ถูกทำลาย
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
    }
    if (this.burstIntervalId) {
      clearInterval(this.burstIntervalId);
    }
  },
  methods: {
    initCanvas() {
      this.canvas = document.getElementById("confettiCanvas_" + this.canvasId);
      this.ctx = this.canvas.getContext("2d");
      this.resizeCanvas();
    },
    initResizeObserver() {
      // สร้าง ResizeObserver instance
      this.resizeObserver = new ResizeObserver((entries) => {
        // entries คือ Array ของ ResizeObserverEntry สำหรับ Element ที่ถูก observe
        for (let entry of entries) {
          if (entry.target === this.$refs.containner) {
            // เรียกเมธอด resizeCanvas ของเราเมื่อ container เปลี่ยนขนาด
            this.resizeCanvas();
          }
        }
      });

      // สั่งให้ ResizeObserver เริ่มเฝ้าดู container ของเรา
      this.resizeObserver.observe(this.$refs.containner);
    },
    resizeCanvas() {
      setTimeout(() => {
        const rect = this.$refs.containner?.getBoundingClientRect();
        this.W = rect?.width;
        this.H = rect?.height;
        this.canvas.width = this.W;
        this.canvas.height = this.H;
      }, 500);
    },
    getRandomColor() {
      const colors = [
        "#FFC0CB",
        "#FF69B4",
        "#FF1493", // Pinks
        "#FF7F50",
        "#FFA07A",
        "#FFDAB9", // Oranges
        "#FFFF00",
        "#FFD700",
        "#DAA520", // Yellows
        "#98FB98",
        "#32CD32",
        "#00FF7F", // Greens
        "#ADD8E6",
        "#87CEFA",
        "#6495ED", // Blues
        "#BA55D3",
        "#9370DB",
        "#8A2BE2", // Purples
      ];
      return colors[Math.floor(Math.random() * colors.length)];
    },

    // สร้างข้อมูลชิ้นส่วน confetti สำหรับการระเบิดหนึ่งครั้ง
    createBurstParticleData(burstX, burstY) {
      const angle = Math.random() * Math.PI * 2; // ทิศทางสุ่ม 360 องศา
      const initialVelocity = Math.random() * this.maxInitialVelocity; // ความเร็วเริ่มต้นที่สุ่ม

      return {
        x: burstX,
        y: burstY,
        w: Math.random() * 8 + 3, // ขนาดเล็กลงหน่อย
        h: Math.random() * 8 + 3,
        vx: Math.cos(angle) * initialVelocity, // ความเร็วแนวแกน X (พุ่งออก)
        vy: Math.sin(angle) * initialVelocity, // ความเร็วแนวแกน Y (พุ่งออก)
        color: this.getRandomColor(),
        opacity: 1,
        rotation: Math.random() * 360,
        rotationSpeed: Math.random() * 0.2 - 0.1, // หมุนเร็วขึ้นหน่อย

        tiltAngle: Math.random() * Math.PI * 2,
        tiltSpeed: Math.random() * 0.1 + 0.02,
        tiltDirection: Math.round(Math.random()),

        lifetime: this.burstDuration, // อายุของ particle เท่ากับระยะเวลาการระเบิด
        age: 0,
        fadeRate: 1 / this.burstDuration, // อัตราการจางหาย
      };
    },

    // อัปเดตสถานะของชิ้นส่วน confetti ในแต่ละเฟรมสำหรับการระเบิด
    updateParticle(piece) {
      piece.vx *= this.drag; // ลดความเร็ว
      piece.vy *= this.drag;
      piece.vy += this.gravity; // เพิ่มแรงโน้มถ่วงเล็กน้อย

      piece.x += piece.vx;
      piece.y += piece.vy;
      piece.rotation += piece.rotationSpeed;
      piece.tiltAngle += piece.tiltSpeed;

      // เพิ่ม age ตาม delta time (สมมติ 60fps)
      // คุณอาจต้องการใช้ delta time จริงๆ เพื่อความแม่นยำ
      piece.age += 1000 / 60;
      piece.opacity = 1 - piece.age / piece.lifetime; // จางหายไปตามอายุ

      if (piece.opacity < 0) piece.opacity = 0; // ไม่ให้ opacity ติดลบ
    },

    // วาดชิ้นส่วน confetti (เหมือนเดิม)
    drawParticle(piece) {
      if (piece.opacity <= 0) return; // ไม่วาดถ้าจางหายไปหมดแล้ว

      this.ctx.save();
      this.ctx.translate(piece.x + piece.w / 2, piece.y + piece.h / 2);
      this.ctx.rotate((piece.rotation * Math.PI) / 180);

      if (piece.tiltDirection === 0) {
        this.ctx.scale(Math.cos(piece.tiltAngle), 1);
      } else {
        this.ctx.scale(1, Math.cos(piece.tiltAngle));
      }

      this.ctx.fillStyle = piece.color;
      this.ctx.globalAlpha = piece.opacity;
      this.ctx.fillRect(-piece.w / 2, -piece.h / 2, piece.w, piece.h);
      this.ctx.restore();
    },

    // ทริกเกอร์การระเบิดใหม่จากตำแหน่งสุ่ม
    triggerBurst() {
      // ลบการระเบิดที่เก่าที่สุดออกหากถึงจำนวนสูงสุดที่ต้องการให้แสดงพร้อมกัน
      // ใน Burst Effect, bursts จะหายไปเองเมื่อ lifetime หมดอายุ
      // ดังนั้นเราอาจไม่จำเป็นต้องใช้ shift() ตรงนี้ แต่จะใช้ filter ใน animate() แทน

      const burstX = Math.random() * this.W;
      const burstY = Math.random() * this.H;
      const particles = [];

      for (let i = 0; i < this.numParticlesPerBurst; i++) {
        particles.push(this.createBurstParticleData(burstX, burstY));
      }

      this.bursts.push({
        particles: particles,
        startTime: Date.now(),
        endTime: Date.now() + this.burstDuration,
      });
    },

    // กำหนดเวลาระเบิดใหม่แบบวนซ้ำ
    scheduleNewBursts() {
      this.burstIntervalId = setInterval(() => {
        // ตรวจสอบจำนวน bursts ปัจจุบันก่อน trigger burst ใหม่
        // เพื่อไม่ให้มี bursts มากเกินไป
        if (this.bursts.length < this.maxBursts) {
          this.triggerBurst();
        }
      }, this.burstInterval);
    },

    // Animation Loop หลัก
    animate() {
      this.ctx.clearRect(0, 0, this.W, this.H);

      const now = Date.now();
      // กรองการระเบิดที่หมดอายุแล้วออก
      // burst.endTime > now หมายถึง bursts ที่ยังไม่หมดอายุ
      this.bursts = this.bursts.filter((burst) => burst.endTime > now);

      this.bursts.forEach((burst) => {
        burst.particles.forEach((piece) => {
          this.updateParticle(piece);
          this.drawParticle(piece);
        });
      });

      this.animationFrameId = requestAnimationFrame(this.animate);
    },
    startAnimation() {
      this.animate();
    },
  },
};
</script>

<style scoped>
.confetti-container {
  width: 100%;
  height: 100%;
  pointer-events: none;
  overflow: hidden;
  z-index: 9999;
}

canvas {
  display: block;
  background-color: transparent;
}
</style>
